import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { roundToPrecision } from '@/helpers/finance';
import {
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { formatDateToNonISOFormat, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import {
  GenerateInvoiceAddedMemberListItem,
  GenerateInvoiceListItem,
  GenerateInvoiceProjectOverheadListItem,
  GenerateInvoiceProjectTeamMemberListItem,
  InvoiceHoursSourceType,
} from '../types';
import { InvoiceEntityTypeDTO } from '../types/dto/invoice-entity-type.dto';
import {
  CreateInvoiceItemRequest,
  CreateInvoiceRequest,
  CreateInvoicesRequest,
} from '../types/dto/request';

/**
 * Maps a team member's invoice data to a CreateInvoiceItemRequest
 */
const mapMemberToInvoiceItem = (
  member: GenerateInvoiceProjectTeamMemberListItem
): CreateInvoiceItemRequest => {
  const memberId = parseInt(member.id, 10);
  const roundedAmount = roundToPrecision(member.total, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const roundedRate = roundToPrecision(
    member.effectiveRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: memberId,
    entityType: InvoiceEntityTypeDTO.USER,
    quantity: member.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

const mapAddedMemberToInvoiceItem = (
  addedMember: GenerateInvoiceAddedMemberListItem
): CreateInvoiceItemRequest | null => {
  if (!addedMember.memberId) return null;

  const roundedAmount = roundToPrecision(
    addedMember.hours * addedMember.effectiveRate,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );
  const roundedRate = roundToPrecision(
    addedMember.effectiveRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: addedMember.memberId,
    entityType: InvoiceEntityTypeDTO.USER,
    quantity: addedMember.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

const mapOverheadToInvoiceItem = (
  overhead: GenerateInvoiceProjectOverheadListItem
): CreateInvoiceItemRequest => {
  const roundedAmount = roundToPrecision(
    overhead.effectiveRate,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

  return {
    entityType: InvoiceEntityTypeDTO.OVERHEAD,
    description: overhead.description,
    amount: roundedAmount.toString(),
    // Scheduled overheads carry their Overheads-page id so the backend marks
    // them INVOICED; ad-hoc lines omit it and get auto-created instead.
    ...(overhead.overheadId !== undefined && { overheadId: overhead.overheadId }),
  };
};

const mapMemberTimeOffToInvoiceItem = (
  member: GenerateInvoiceProjectTeamMemberListItem
): CreateInvoiceItemRequest | null => {
  const timeOffSource = member.hoursSources.find(
    s => s.sourceType === InvoiceHoursSourceType.TIME_OFF
  );
  if (!timeOffSource || timeOffSource.hours <= 0) return null;

  const memberId = parseInt(member.id, 10);
  const roundedAmount = roundToPrecision(
    timeOffSource.amount,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );
  const roundedRate = roundToPrecision(
    timeOffSource.averageRate,
    FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
  );

  return {
    entityId: memberId,
    entityType: InvoiceEntityTypeDTO.USER,
    quantity: timeOffSource.hours.toString(),
    unitPrice: roundedRate.toString(),
    amount: roundedAmount.toString(),
  };
};

/**
 * Maps a project's invoice data to a CreateInvoiceRequest
 */
const mapProjectToInvoiceRequest = (
  project: GenerateInvoiceListItem,
  billingPeriodString: string
): CreateInvoiceRequest => {
  const dateRange = parseDateRange(billingPeriodString);

  if (!dateRange?.startDate || !dateRange?.endDate) {
    throw new Error('Invalid billing period');
  }

  // TODO: Time-off items are temporarily excluded from invoice creation because
  // BOOKED hours already have time-off subtracted in the BE builder, so adding
  // separate time-off line items causes double-counting and duplicate QuickBooks entries.
  const memberItems: CreateInvoiceItemRequest[] = project.projectMembers.flatMap(member => {
    const mainItem = mapMemberToInvoiceItem(member);
    // const timeOffItem = mapMemberTimeOffToInvoiceItem(member);
    // return timeOffItem ? [mainItem, timeOffItem] : [mainItem];
    return [mainItem];
  });

  const addedMemberItems: CreateInvoiceItemRequest[] =
    project.projectAddedMembers
      ?.map(mapAddedMemberToInvoiceItem)
      .filter((item): item is CreateInvoiceItemRequest => item !== null) ?? [];

  const overheadItems: CreateInvoiceItemRequest[] =
    project.projectOverheads?.map(mapOverheadToInvoiceItem) ?? [];

  return {
    projectId: project.project.id,
    startDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(dateRange.startDate)),
    endDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(dateRange.endDate)),
    currencyCode: project.currency.code,
    items: [...memberItems, ...addedMemberItems, ...overheadItems],
    // Key must be absent (not null) when the payment-details feature flag is off
    ...(project.paymentDetailsId !== undefined && { paymentDetailsId: project.paymentDetailsId }),
  };
};

/**
 * Maps invoice projects (with selected members and form values) to CreateInvoicesRequest
 *
 * @param invoiceProjects - Array of projects with their selected team members and actual form values
 * @param billingPeriod - Billing period filter value (string representation of date range)
 * @returns CreateInvoicesRequest ready to send to API
 */
export const mapGenerateInvoicesToRequest = (
  invoiceProjects: GenerateInvoiceListItem[],
  billingPeriod: string
): CreateInvoicesRequest => {
  const invoices: CreateInvoiceRequest[] = invoiceProjects.map(project =>
    mapProjectToInvoiceRequest(project, billingPeriod)
  );

  return {
    invoices,
  };
};
